import React, { useEffect, useState } from 'react';
import { Modal, ModalContent } from '@fiverr-private/overlay';
import { Layout, Stack } from '@fiverr-private/layout_components';
import { Button, IconButton } from '@fiverr-private/button';
import { TimesIcon } from '@fiverr-private/visuals';
import { Typography } from '@fiverr-private/typography';
import { raiseToast } from '@fiverr-private/toast';
import { appTranslate } from '../../i18n';
import { useBI, maskEmail } from '../../bi';
import { contactSchema } from './steps/contactSchema';
// eslint-disable-next-line no-duplicate-imports
import type { ContactInfo } from './steps/contactSchema';
import { WizardStepKind, type AllAnswerKey, type QuestionStepId } from './types';
import { WIZARD_STEPS } from './wizard.config';
import { WIZARD_GLOW } from './glow.config';
import { WizardProgress } from './WizardProgress';
import { WizardGlow } from './WizardGlow';
import { glowEnter } from './styles.ve.css';

interface WizardState {
    stepIndex: number;
    maxReachedIndex: number;
    answers: Record<AllAnswerKey, string>;
}

export interface WizardSubmission {
    answers: Record<AllAnswerKey, string>;
}

type SubmitState = 'idle' | 'submitting' | 'error';

export interface UnusualWayWizardProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (submission: WizardSubmission) => Promise<void>;
}

const initialState = (): WizardState => ({
    stepIndex: 0,
    maxReachedIndex: 0,
    answers: {
        unexpected: '',
        made: '',
        proud: '',
        about: '',
        name: '',
        email: '',
        location: '',
        linkedin: '',
    },
});

export const UnusualWayWizard = ({ isOpen, onClose, onSubmit }: UnusualWayWizardProps) => {
    const bi = useBI();
    const [state, setState] = useState(initialState);
    const [submitState, setSubmitState] = useState<SubmitState>('idle');
    // Deferred until the modal's enter animation ends — the glow's first paint is expensive
    // (large blurred SVG), and painting it during the entrance drops the animation's frames.
    const [isGlowVisible, setIsGlowVisible] = useState(false);
    const { stepIndex, maxReachedIndex, answers } = state;

    const step = WIZARD_STEPS[stepIndex];

    // The wizard is mounted unconditionally by the parent (isOpen just toggles the modal), so gate
    // on `isOpen` to avoid firing false impressions on initial mount / after close-and-reset.
    useEffect(() => {
        if (!isOpen) {
            return;
        }
        bi.reportUnusualWayStepImpression({ stepId: step.id });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step.id, isOpen]);

    const isQuestionFilled =
        step.kind === WizardStepKind.Question && answers[step.id as QuestionStepId].trim().length > 0;
    const isFormValid = step.kind === WizardStepKind.Form && contactSchema.safeParse(answers).success;
    const canAdvance = isQuestionFilled || isFormValid || step.kind === WizardStepKind.End;

    const handleClose = () => {
        onClose();
        setTimeout(() => {
            setState(initialState);
            setSubmitState('idle');
            setIsGlowVisible(false);
        }, 1000);
    };
    const handleNext = async () => {
        if (!canAdvance || submitState === 'submitting') {
            return;
        }
        if (step.kind === WizardStepKind.Form) {
            setSubmitState('submitting');
            try {
                await onSubmit({ answers });
                bi.reportUnusualWayStepSubmit({ stepId: step.id, maskedValue: maskEmail(answers.email) });
                setState((s) => ({
                    ...s,
                    stepIndex: s.stepIndex + 1,
                    maxReachedIndex: Math.max(s.maxReachedIndex, s.stepIndex + 1),
                }));
                setSubmitState('idle');
            } catch {
                setSubmitState('error');
                raiseToast({
                    message: appTranslate('unusual_way_wizard.submission.error'),
                    type: 'error',
                    location: 'top',
                });
            }
            return;
        }
        if (step.kind === WizardStepKind.Question) {
            // Free-text answers are never sent — stepId only, no answer content (PII rule).
            bi.reportUnusualWayStepSubmit({ stepId: step.id });
        }
        const next = Math.min(WIZARD_STEPS.length - 1, stepIndex + 1);
        setState((s) => ({ ...s, stepIndex: next, maxReachedIndex: Math.max(s.maxReachedIndex, next) }));
    };

    return (
        <Modal isOpen={isOpen} onClose={handleClose} theme="dark" fullScreenBreakpoint="all">
            <ModalContent
                closeOnEscapeKey
                onAnimationEnd={() => {
                    if (isOpen) {
                        setIsGlowVisible(true);
                    }
                }}
            >
                {/* Canvas — flex column, centers inner stack vertically */}
                <Stack
                    direction="column"
                    justifyContent="center"
                    alignItems="center"
                    position="relative"
                    width="100%"
                    minHeight="100%"
                    overflow="clip"
                >
                    {isGlowVisible && (
                        <Layout className={glowEnter} position="absolute" pointerEvents="none" style={{ inset: 0 }}>
                            <WizardGlow
                                rotation={WIZARD_GLOW[step.id].rotation}
                                left={WIZARD_GLOW[step.id].left}
                                top={WIZARD_GLOW[step.id].top}
                            />
                        </Layout>
                    )}
                    <IconButton
                        size="xs"
                        variant="ghost"
                        colorScheme="white"
                        aria-label={appTranslate('unusual_way_wizard.close') as string}
                        onClick={handleClose}
                        position="absolute"
                        top={{ default: '22px', md: '32px' }}
                        right={{ default: '22px', md: '32px' }}
                        style={{ zIndex: 1 }}
                    >
                        <TimesIcon />
                    </IconButton>
                    {/* Content block — natural height, vertically centered by parent */}
                    <Stack
                        direction="column"
                        position="relative"
                        gap="12"
                        paddingX={{ default: '6', md: '16' }}
                        paddingY={{ default: '20', md: '32' }}
                        maxWidth="1400px"
                        width="100%"
                        style={{ boxSizing: 'border-box' }}
                    >
                        {step.kind !== WizardStepKind.End && (
                            <WizardProgress
                                count={WIZARD_STEPS.filter((s) => s.kind !== WizardStepKind.End).length}
                                activeIndex={stepIndex}
                                maxReachedIndex={maxReachedIndex}
                                onNavigate={(index) => setState((s) => ({ ...s, stepIndex: index }))}
                            />
                        )}
                        {step.kind === WizardStepKind.Question && (
                            <step.Component
                                value={answers[step.id as QuestionStepId]}
                                onChange={(v: string) =>
                                    setState((s) => ({ ...s, answers: { ...s.answers, [step.id]: v } }))
                                }
                            />
                        )}
                        {step.kind === WizardStepKind.Form && (
                            <step.Component
                                contact={
                                    {
                                        name: answers.name,
                                        email: answers.email,
                                        location: answers.location,
                                        linkedin: answers.linkedin,
                                    } as ContactInfo
                                }
                                onChange={(c: ContactInfo) =>
                                    setState((s) => ({ ...s, answers: { ...s.answers, ...c } }))
                                }
                            />
                        )}
                        {step.kind === WizardStepKind.End && <step.Component />}
                        {step.kind !== WizardStepKind.End && (
                            <Stack
                                direction={{ default: 'column', md: 'row' }}
                                justifyContent={{ md: 'flexEnd' }}
                                alignItems={{ md: 'center' }}
                                gap="4"
                                width="100%"
                            >
                                {submitState === 'error' && (
                                    <Typography as="p" size="b_sm" color="validationErrorDark">
                                        {appTranslate('unusual_way_wizard.submission.error')}
                                    </Typography>
                                )}
                                <Button
                                    colorScheme="white"
                                    size="lg"
                                    disabled={!canAdvance || submitState === 'submitting'}
                                    loading={submitState === 'submitting'}
                                    onClick={handleNext}
                                    fullWidth={{ default: true, md: false }}
                                    style={{ borderRadius: 9999 }}
                                >
                                    {step.kind === WizardStepKind.Form
                                        ? appTranslate('unusual_way_wizard.submission.cta')
                                        : `${appTranslate('unusual_way_wizard.next')} →`}
                                </Button>
                            </Stack>
                        )}
                    </Stack>
                </Stack>
            </ModalContent>
        </Modal>
    );
};
